/// <reference path="./global.d.ts" />
import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { getChatbotSdkUrl } from './config';
import { fetchChatbotGrayConfig } from './grayService';
import { useChatbot } from './useChatbot';
import type { ChatbotEnv, ChatbotGrayGateProps } from './types';

const ZENDESK_LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  'zh-tw': 'zh-tw',
  'uk-ua': 'en',
  es: 'es',
  ru: 'ru',
  vi: 'vi',
  de: 'de',
  tr: 'tr',
  fr: 'fr',
  pt: 'pt',
  ar: 'ar',
};

function ChatbotInit({
  theme,
  env,
  pageType,
}: {
  theme: 'dark' | 'light';
  env: ChatbotEnv;
  pageType?: 'trade' | 'default';
}) {
  const token = env.getToken() || '';
  useChatbot({ theme, env, token, pageType });
  return null;
}

function injectChatbotSdk(sdkUrl: string) {
  if (document.getElementById('chatbot-sdk')) return;

  const polyfill = document.createElement('script');
  polyfill.id = 'chatbot-process-polyfill';
  polyfill.textContent =
    'if(typeof window!=="undefined"&&!window.process){window.process={env:{NODE_ENV:"production"}}}';
  document.body.appendChild(polyfill);

  const t = Math.floor(Date.now() / 300000);
  const script = document.createElement('script');
  script.id = 'chatbot-sdk';
  script.async = true;
  script.src = `${sdkUrl}?t=${t}`;
  document.body.appendChild(script);
}

function injectZendeskSdk(zendeskID: string, locale: string, hide = false) {
  if (document.getElementById('ze-snippet')) return;

  const script = document.createElement('script');
  script.id = 'ze-snippet';
  script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskID}`;
  script.onload = () => {
    try {
      if ('zE' in window) {
        (window as any).zE('messenger:set', 'locale', ZENDESK_LOCALE_MAP[locale] || 'en-US');
        if (hide) {
          (window as any).zE('messenger', 'hide');
          (window as any).zE('messenger:on', 'close', () => {
            (window as any).zE('messenger', 'hide');
          });
        }
      }
    } catch (err) {
      console.warn('[zendesk] set locale failed', err);
    }
  };
  document.body.appendChild(script);
}

export function ChatbotGrayGate({
  theme,
  env,
  isKrSite = false,
  pageType,
  hideZendesk = false,
  isSubAccount,
}: ChatbotGrayGateProps) {
  const [result, setResult] = useState<'chatbot' | 'zendesk' | null>(null);
  const { locale } = useRouter();
  /**
   * 一次会话内只分流一次。
   *
   * 前提（产品约束）：
   *   主账户与子账户之间的切换会触发整页 reload，导致 React 树重建、本组件重新挂载、
   *   decidedRef 自然重置。因此 SPA 内 isSubAccount 不会发生 false ↔ true 的变化。
   *
   * 若未来产品改成 SPA 内可切换账号，需要：
   *   1. 移除 decidedRef，让 effect 依赖 isSubAccount 重新触发分流；
   *   2. 实现 chatbot → zendesk（及反向）的 SDK 卸载逻辑，避免两套客服 SDK 并存。
   */
  const decidedRef = useRef(false);

  useEffect(() => {
    if (isKrSite) return;
    if (decidedRef.current) return;

    // 已登录态首屏（有 token）若 userInfo 还在加载（isSubAccount=undefined），
    // 必须等到账号类型明确再分流；否则子账户会被误注入 Chatbot 且无法回切 Zendesk。
    // 未登录态（无 token）userInfo 永不加载，直接按主账户走灰度分流，保证 /login 等
    // 页面也有客服入口。
    if (isSubAccount === undefined && !!env.getToken()) return;

    decidedRef.current = true;

    if (isSubAccount === true) {
      injectZendeskSdk(env.zendeskID, locale ?? 'en', hideZendesk);
      setResult('zendesk');
      return;
    }

    // 注意：不要用 setTimeout 包装并在 cleanup 中 clearTimeout —— 在 React StrictMode
    // 下首次 effect 的 cleanup 会立即取消 timer，第二次 effect 又被 decidedRef 拦截，
    // 导致 fetch 永远不发起。直接发起 fetch 即可，decidedRef 已保证只跑一次。
    fetchChatbotGrayConfig(env.baseUrl, env.getToken).then((hitGray) => {
      if (hitGray) {
        injectChatbotSdk(getChatbotSdkUrl(env.navUrl, env.cdnFallback));
        setResult('chatbot');
      } else {
        injectZendeskSdk(env.zendeskID, locale ?? 'en', hideZendesk);
        setResult('zendesk');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubAccount]);

  if (isKrSite) return null;

  if (result === 'chatbot') {
    return <ChatbotInit theme={theme} env={env} pageType={pageType} />;
  }

  return null;
}
